import { useEffect, useRef, useState } from "react";

/**
 * A React hook that “smoothly” reveals new text over time, at a fixed
 * number of characters per frame, but instantly flushes the remainder
 * whenever the page is put in the background.
 *
 * @param content
 *   The full string you ultimately want displayed. Whenever this
 *   value changes, the hook will determine which part of it is new
 *   (the “delta” since last render) and enqueue that for streaming.
 *
 * @param opts
 *   Configuration options:
 *     - pause: if truthy, halts any ongoing streaming and stops new
 *              streams from starting until set back to false.
 *     - charsPerFrame: how many characters to append each rAF tick
 *                      (default: 3).
 *
 * @returns
 *   A string which initially matches the first `content` passed in,
 *   and then, whenever `content` grows by appending characters,
 *   gradually reveals those new characters at `charsPerFrame` per
 *   animation frame—as long as the tab is visible. If the tab is
 *   hidden, whatever is left in the “pending” buffer is flushed in
 *   one go.
 */
export const useSmoothStreaming = (content: string, opts: { pause?: boolean; charsPerFrame?: number } = {}): string => {
  // The string we actually render to the UI
  const [displayedContent, setDisplayedContent] = useState(content);

  // Buffer of “new” characters waiting to be streamed
  const pendingChars = useRef<string>("");

  // rAF handle so we can cancel on unmount or prop-change
  const frameRef = useRef<number>();

  // Remember the last value of `content` so we can diff
  const lastContentRef = useRef<string>(content);

  const charsPerFrame = opts.charsPerFrame ?? 3;

  useEffect(() => {
    if (frameRef.current !== undefined) {
      cancelAnimationFrame(frameRef.current);
    }

    if (opts.pause) {
      return;
    }

    // -- If the new `content` is not just an extension of the last one --
    if (!content.startsWith(lastContentRef.current)) {
      // Reset immediately to the new string (drop any buffer)
      setDisplayedContent(content);
      lastContentRef.current = content;
      pendingChars.current = "";
      return;
    }

    // -- Otherwise, queue only the “delta” since last render --
    pendingChars.current += content.slice(lastContentRef.current.length);
    lastContentRef.current = content;

    /**
     * scheduleNext()
     *
     * Decides whether to:
     *  - rAF-step (when visible), or
     *  - flush-all (when hidden).
     */
    const scheduleNext = () => {
      if (document.hidden) {
        console.debug("[smooth streaming] page is backgrounded →  the rest of the buffer");
        // Page is backgrounded → dump the rest of the buffer
        setDisplayedContent(content);
        lastContentRef.current = content;
        pendingChars.current = "";
      } else {
        // Page is visible → do another animation‐frame step
        frameRef.current = requestAnimationFrame(step);
      }
    };

    /**
     * step()
     *
     * Called on each animation frame while the tab is visible.
     * Appends `charsPerFrame` characters, then reschedules.
     */
    const step = () => {
      if (pendingChars.current.length > 0) {
        // Slice off a chunk, update buffer, push to UI
        const chunk = pendingChars.current.slice(0, charsPerFrame);
        pendingChars.current = pendingChars.current.slice(charsPerFrame);
        setDisplayedContent((prev) => prev + chunk);

        // Continue streaming until the buffer is empty
        scheduleNext();
      }
    };

    // Kick off streaming if there's anything waiting
    if (pendingChars.current.length > 0) {
      scheduleNext();
    }

    return () => {
      if (frameRef.current !== undefined) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [content, opts.pause, charsPerFrame]);

  return displayedContent;
};
